import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthSession } from './AuthSessionContext';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axiosInstance from './axiosInstance';

const normaliseStringArray = (value = []) => [...value].map((item) => item || '').sort();

const DEFAULT_SUBCONTRACTED_BUDGET = '4.99';
const DEFAULT_ADAS_BUDGET = '1.80';
const DEFAULT_STORAGE_RECOVERY_BUDGET = '1.88';

const createEmptyForm = () => ({
  site_budget: '',
  parts_budget: '',
  parts_alert_threshold: '',
  genuine_parts_budget: '',
  genuine_parts_alert_threshold: '',
  non_genuine_parts_budget: '',
  non_genuine_parts_alert_threshold: '',
  green_parts_budget: '',
  green_parts_alert_threshold: '',
  subcontracted_budget: DEFAULT_SUBCONTRACTED_BUDGET,
  subcontracted_alert_threshold: DEFAULT_SUBCONTRACTED_BUDGET,
  subcontracted_adas_calibration_diagnostics_budget: DEFAULT_ADAS_BUDGET,
  subcontracted_adas_calibration_diagnostics_alert_threshold: DEFAULT_ADAS_BUDGET,
  subcontracted_general_budget: '',
  subcontracted_general_alert_threshold: '',
  subcontracted_lock_set_budget: '',
  subcontracted_lock_set_alert_threshold: '',
  subcontracted_signwriting_graphics_budget: '',
  subcontracted_signwriting_graphics_alert_threshold: '',
  subcontracted_tyres_alloy_wheels_budget: '',
  subcontracted_tyres_alloy_wheels_alert_threshold: '',
  subcontracted_windscreen_repairs_budget: '',
  subcontracted_windscreen_repairs_alert_threshold: '',
  storage_recovery_budget: DEFAULT_STORAGE_RECOVERY_BUDGET,
  storage_recovery_alert_threshold: DEFAULT_STORAGE_RECOVERY_BUDGET,
  notify_users: [],
  budgetId: null,
  created_on: '',
  created_by: '',
  updated_on: '',
  updated_by: '',
});

const hasCustomAlert = (values, budgetField, alertField) => {
  if (!values) return false;
  const alert = Number(values[alertField]);
  const budget = Number(values[budgetField]);
  if (!values[alertField]) return false;
  if (Number.isNaN(alert) || Number.isNaN(budget)) return false;
  return Math.abs(alert - budget) > 1e-6;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const formatDateOnly = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (err) {
    return value;
  }
};

const buildBudgetPeriods = ({ pastMonths = 12, futureMonths = 12 } = {}) => {
  const now = new Date();
  const periods = [];
  for (let offset = -pastMonths; offset <= futureMonths; offset += 1) {
    const periodDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    periods.push({
      label: periodDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      month: periodDate.getMonth() + 1,
      year: periodDate.getFullYear(),
      value: `${periodDate.getFullYear()}-${String(periodDate.getMonth() + 1).padStart(2, '0')}`,
      isCurrent: offset === 0,
    });
  }
  return periods;
};

const percentPattern = /^\d{0,3}(?:\.\d{0,2})?$/;
const siteBudgetPattern = /^\d{0,6}(?:\.\d{0,2})?$/;

const normalizePercentValue = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  const rounded = Math.round(numeric * 100) / 100;
  return Number.isInteger(rounded) ? String(Math.trunc(rounded)) : rounded.toFixed(2);
};

const formatSiteBudgetDisplay = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const getPreviousBudgetPeriod = (period) => {
  if (!period?.month || !period?.year) return null;
  const previousDate = new Date(period.year, period.month - 2, 1);
  return {
    label: previousDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    month: previousDate.getMonth() + 1,
    year: previousDate.getFullYear(),
    value: `${previousDate.getFullYear()}-${String(previousDate.getMonth() + 1).padStart(2, '0')}`,
    isCurrent: false,
  };
};

const buildBudgetPayloadFromSource = ({ budget, locationId, period }) => ({
  location_id: locationId,
  budget_month: period.month,
  budget_year: period.year,
  site_budget: budget.site_budget,
  parts_budget: budget.parts_budget,
  parts_alert_threshold: budget.parts_alert_threshold,
  genuine_parts_budget: budget.genuine_parts_budget,
  genuine_parts_alert_threshold: budget.genuine_parts_alert_threshold,
  non_genuine_parts_budget: budget.non_genuine_parts_budget,
  non_genuine_parts_alert_threshold: budget.non_genuine_parts_alert_threshold,
  green_parts_budget: budget.green_parts_budget,
  green_parts_alert_threshold: budget.green_parts_alert_threshold,
  subcontracted_budget: budget.subcontracted_budget,
  subcontracted_alert_threshold: budget.subcontracted_alert_threshold,
  subcontracted_adas_calibration_diagnostics_budget: budget.subcontracted_adas_calibration_diagnostics_budget,
  subcontracted_adas_calibration_diagnostics_alert_threshold: budget.subcontracted_adas_calibration_diagnostics_alert_threshold,
  subcontracted_general_budget: budget.subcontracted_general_budget,
  subcontracted_general_alert_threshold: budget.subcontracted_general_alert_threshold,
  subcontracted_lock_set_budget: budget.subcontracted_lock_set_budget,
  subcontracted_lock_set_alert_threshold: budget.subcontracted_lock_set_alert_threshold,
  subcontracted_signwriting_graphics_budget: budget.subcontracted_signwriting_graphics_budget,
  subcontracted_signwriting_graphics_alert_threshold: budget.subcontracted_signwriting_graphics_alert_threshold,
  subcontracted_tyres_alloy_wheels_budget: budget.subcontracted_tyres_alloy_wheels_budget,
  subcontracted_tyres_alloy_wheels_alert_threshold: budget.subcontracted_tyres_alloy_wheels_alert_threshold,
  subcontracted_windscreen_repairs_budget: budget.subcontracted_windscreen_repairs_budget,
  subcontracted_windscreen_repairs_alert_threshold: budget.subcontracted_windscreen_repairs_alert_threshold,
  storage_recovery_budget: budget.storage_recovery_budget,
  storage_recovery_alert_threshold: budget.storage_recovery_alert_threshold,
  notify_users: Array.isArray(budget.notify_users) ? budget.notify_users : [],
});

const BudgetPage = () => {
  const [locations, setLocations] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [previousMonthBudgets, setPreviousMonthBudgets] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formValues, setFormValues] = useState(createEmptyForm());
  const [initialFormValues, setInitialFormValues] = useState(createEmptyForm());
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBudget, setUploadingBudget] = useState(false);
  const budgetFileInputRef = useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [defaultNotifyUsers, setDefaultNotifyUsers] = useState([]);
  const [defaultEmail, setDefaultEmail] = useState('');
  const [defaultSaving, setDefaultSaving] = useState(false);
  const [subcontractedNominalCodes, setSubcontractedNominalCodes] = useState([]);
  const budgetPeriodOptions = useMemo(() => buildBudgetPeriods(), []);
  const [selectedBudgetPeriod, setSelectedBudgetPeriod] = useState(null);
  const [defaultSettingsMeta, setDefaultSettingsMeta] = useState({
    created_on: '',
    created_by: '',
    updated_on: '',
    updated_by: '',
  });
  const [subcontractedFieldDefs, setSubcontractedFieldDefs] = useState([]);
  const [initialDefaultNotifyUsers, setInitialDefaultNotifyUsers] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', message: '' });
  const [isPartsAlertManuallyEdited, setIsPartsAlertManuallyEdited] = useState(false);
  const [isGenuineAlertManuallyEdited, setIsGenuineAlertManuallyEdited] = useState(false);
  const [isNonGenuineAlertManuallyEdited, setIsNonGenuineAlertManuallyEdited] = useState(false);
  const [isGreenAlertManuallyEdited, setIsGreenAlertManuallyEdited] = useState(false);
  const [isSubcontractedAlertManuallyEdited, setIsSubcontractedAlertManuallyEdited] = useState(false);
  const [isStorageAlertManuallyEdited, setIsStorageAlertManuallyEdited] = useState(false);
  const [subcontractedAlertOverrides, setSubcontractedAlertOverrides] = useState({});
  const [isSiteBudgetFocused, setIsSiteBudgetFocused] = useState(false);
  const { roles: userRoles } = useAuthSession();
  const canEditBudgets = userRoles.includes('admin');
  const readOnly = !canEditBudgets;
  const previousBudgetPeriod = useMemo(
    () => getPreviousBudgetPeriod(selectedBudgetPeriod),
    [selectedBudgetPeriod]
  );
  const excludedSubcontractedDefs = useMemo(
    () => subcontractedFieldDefs.filter((def) => !subcontractedNominalCodes.includes(def.code)),
    [subcontractedNominalCodes, subcontractedFieldDefs]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const budgetParams = selectedBudgetPeriod
        ? { budget_month: selectedBudgetPeriod.month, budget_year: selectedBudgetPeriod.year }
        : null;
      const previousBudgetParams = previousBudgetPeriod
        ? { budget_month: previousBudgetPeriod.month, budget_year: previousBudgetPeriod.year }
        : null;
      const [locationsResponse, budgetsResponse, previousBudgetsResponse, settingsResponse] = await Promise.all([
        axiosInstance.get('/api/v1/location'),
        budgetParams
          ? axiosInstance.get('/api/v1/budget', { params: budgetParams })
          : Promise.resolve({ data: { data: [] } }),
        previousBudgetParams
          ? axiosInstance.get('/api/v1/budget', { params: previousBudgetParams })
          : Promise.resolve({ data: { data: [] } }),
        axiosInstance.get('/api/v1/budget/settings'),
      ]);
      setLocations(
        [...(locationsResponse?.data?.data || [])].sort((a, b) => {
          const nameA = (a?.name || a?.location_id || '').toString().toLowerCase();
          const nameB = (b?.name || b?.location_id || '').toString().toLowerCase();
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        })
      );
      setBudgets(budgetsResponse?.data?.data || []);
      setPreviousMonthBudgets(previousBudgetsResponse?.data?.data || []);
      const budgetMeta = budgetsResponse?.data?.meta || {};
      setSubcontractedNominalCodes(budgetMeta.subcontracted_nominal_codes || []);
      const rawChildren = Array.isArray(budgetMeta.subcontracted_children)
        ? budgetMeta.subcontracted_children
        : [];
      setSubcontractedFieldDefs(
        rawChildren
          .map((child) => ({
            code: child?.code,
            label: child?.label,
            budgetField: child?.budget_field ?? child?.budgetField,
            alertField: child?.alert_field ?? child?.alertField,
          }))
          .filter((child) => child.code && child.budgetField && child.alertField)
      );
      const settingsData = settingsResponse?.data?.data || {};
      const initialDefaults = settingsData.default_notify_users || [];
      setDefaultNotifyUsers(initialDefaults);
      setInitialDefaultNotifyUsers(initialDefaults);
      setDefaultSettingsMeta({
        created_on: settingsData.created_on || '',
        created_by: settingsData.created_by || '',
        updated_on: settingsData.updated_on || '',
        updated_by: settingsData.updated_by || '',
      });
    } catch (err) {
      const message = err?.response?.data?.error || 'Unable to load budget data.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [previousBudgetPeriod, selectedBudgetPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!selectedBudgetPeriod && budgetPeriodOptions.length) {
      const current = budgetPeriodOptions.find((period) => period.isCurrent) || budgetPeriodOptions[0];
      setSelectedBudgetPeriod(current);
    }
  }, [budgetPeriodOptions, selectedBudgetPeriod]);

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [success]);

  const handleErrorClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setError('');
  };

  const handleSuccessClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSuccess('');
  };

  const budgetsByLocation = useMemo(() => {
    const map = new Map();
    budgets.forEach((budget) => {
      if (budget?.location_id) {
        map.set(budget.location_id, budget);
      }
    });
    return map;
  }, [budgets]);

  const selectedLocationPreviousBudget = useMemo(() => {
    if (!selectedLocation?.location_id || !previousBudgetPeriod) return null;
    return previousMonthBudgets.find(
      (budget) =>
        budget?.location_id === selectedLocation.location_id
    ) || null;
  }, [previousMonthBudgets, previousBudgetPeriod, selectedLocation]);

  const availablePreviousMonthBudgets = useMemo(() => {
    if (!previousBudgetPeriod) return [];
    return previousMonthBudgets.filter((budget) => budget?.location_id);
  }, [previousMonthBudgets, previousBudgetPeriod]);

  const buildFormState = useCallback(
    (locationId) => {
      const budget = budgetsByLocation.get(locationId);
      const resolvedSiteBudget =
        budget?.site_budget !== undefined && budget?.site_budget !== null
          ? budget.site_budget
          : undefined;
      const resolvedPartsBudget = budget?.parts_budget;
      const resolvedPartsAlert = budget?.parts_alert_threshold;
      const resolvedGenuineBudget = budget?.genuine_parts_budget;
      const resolvedGenuineAlert = budget?.genuine_parts_alert_threshold;
      const resolvedNonGenuineBudget = budget?.non_genuine_parts_budget;
      const resolvedNonGenuineAlert = budget?.non_genuine_parts_alert_threshold;
      const resolvedGreenBudget = budget?.green_parts_budget;
      const resolvedGreenAlert = budget?.green_parts_alert_threshold;
      const resolvedSubcontractedBudget = budget?.subcontracted_budget;
      const resolvedSubcontractedAlert = budget?.subcontracted_alert_threshold;
      const resolvedAdasBudget = budget?.subcontracted_adas_calibration_diagnostics_budget;
      const resolvedAdasAlert = budget?.subcontracted_adas_calibration_diagnostics_alert_threshold;
      const resolvedSubcontractedGeneralBudget = budget?.subcontracted_general_budget;
      const resolvedSubcontractedGeneralAlert = budget?.subcontracted_general_alert_threshold;
      const resolvedSubcontractedLockSetBudget = budget?.subcontracted_lock_set_budget;
      const resolvedSubcontractedLockSetAlert = budget?.subcontracted_lock_set_alert_threshold;
      const resolvedSubcontractedSignwritingBudget = budget?.subcontracted_signwriting_graphics_budget;
      const resolvedSubcontractedSignwritingAlert = budget?.subcontracted_signwriting_graphics_alert_threshold;
      const resolvedSubcontractedTyresBudget = budget?.subcontracted_tyres_alloy_wheels_budget;
      const resolvedSubcontractedTyresAlert = budget?.subcontracted_tyres_alloy_wheels_alert_threshold;
      const resolvedSubcontractedWindscreenBudget = budget?.subcontracted_windscreen_repairs_budget;
      const resolvedSubcontractedWindscreenAlert = budget?.subcontracted_windscreen_repairs_alert_threshold;
      const resolvedStorageBudget = budget?.storage_recovery_budget;
      const resolvedStorageAlert = budget?.storage_recovery_alert_threshold;

      return {
        budgetId: budget?.id || null,
        site_budget:
          resolvedSiteBudget !== undefined && resolvedSiteBudget !== null
            ? String(resolvedSiteBudget)
            : '',
        parts_budget: normalizePercentValue(resolvedPartsBudget),
        parts_alert_threshold: normalizePercentValue(resolvedPartsAlert),
        genuine_parts_budget: normalizePercentValue(resolvedGenuineBudget),
        genuine_parts_alert_threshold: normalizePercentValue(resolvedGenuineAlert),
        non_genuine_parts_budget: normalizePercentValue(resolvedNonGenuineBudget),
        non_genuine_parts_alert_threshold: normalizePercentValue(resolvedNonGenuineAlert),
        green_parts_budget: normalizePercentValue(resolvedGreenBudget),
        green_parts_alert_threshold: normalizePercentValue(resolvedGreenAlert),
        subcontracted_budget: budget ? normalizePercentValue(resolvedSubcontractedBudget) : DEFAULT_SUBCONTRACTED_BUDGET,
        subcontracted_alert_threshold: budget ? normalizePercentValue(resolvedSubcontractedAlert) : DEFAULT_SUBCONTRACTED_BUDGET,
        subcontracted_adas_calibration_diagnostics_budget: budget ? normalizePercentValue(resolvedAdasBudget) : DEFAULT_ADAS_BUDGET,
        subcontracted_adas_calibration_diagnostics_alert_threshold: budget ? normalizePercentValue(resolvedAdasAlert) : DEFAULT_ADAS_BUDGET,
        subcontracted_general_budget: normalizePercentValue(resolvedSubcontractedGeneralBudget),
        subcontracted_general_alert_threshold: normalizePercentValue(resolvedSubcontractedGeneralAlert),
        subcontracted_lock_set_budget: normalizePercentValue(resolvedSubcontractedLockSetBudget),
        subcontracted_lock_set_alert_threshold: normalizePercentValue(resolvedSubcontractedLockSetAlert),
        subcontracted_signwriting_graphics_budget: normalizePercentValue(resolvedSubcontractedSignwritingBudget),
        subcontracted_signwriting_graphics_alert_threshold: normalizePercentValue(resolvedSubcontractedSignwritingAlert),
        subcontracted_tyres_alloy_wheels_budget: normalizePercentValue(resolvedSubcontractedTyresBudget),
        subcontracted_tyres_alloy_wheels_alert_threshold: normalizePercentValue(resolvedSubcontractedTyresAlert),
        subcontracted_windscreen_repairs_budget: normalizePercentValue(resolvedSubcontractedWindscreenBudget),
        subcontracted_windscreen_repairs_alert_threshold: normalizePercentValue(resolvedSubcontractedWindscreenAlert),
        storage_recovery_budget: budget ? normalizePercentValue(resolvedStorageBudget) : DEFAULT_STORAGE_RECOVERY_BUDGET,
        storage_recovery_alert_threshold: budget ? normalizePercentValue(resolvedStorageAlert) : DEFAULT_STORAGE_RECOVERY_BUDGET,
        notify_users: Array.isArray(budget?.notify_users) ? [...budget.notify_users] : [],
        created_on: budget?.created_on || '',
        created_by: budget?.created_by || '',
        updated_on: budget?.updated_on || '',
        updated_by: budget?.updated_by || '',
      };
    },
    [budgetsByLocation]
  );

  useEffect(() => {
    if (!locations.length) {
      setSelectedLocation(null);
      setFormValues(createEmptyForm());
      setIsPartsAlertManuallyEdited(false);
      setIsGenuineAlertManuallyEdited(false);
      setIsNonGenuineAlertManuallyEdited(false);
      setIsGreenAlertManuallyEdited(false);
      setIsSubcontractedAlertManuallyEdited(false);
      setIsStorageAlertManuallyEdited(false);
      setSubcontractedAlertOverrides({});
      setIsSiteBudgetFocused(false);
      setNewEmail('');
      return;
    }

    if (!selectedLocation) {
      setSelectedLocation(locations[0]);
    }
  }, [locations, selectedLocation]);

  useEffect(() => {
    if (!selectedLocation) {
      setFormValues(createEmptyForm());
      setInitialFormValues(createEmptyForm());
      setNewEmail('');
      setIsPartsAlertManuallyEdited(false);
      setIsGenuineAlertManuallyEdited(false);
      setIsNonGenuineAlertManuallyEdited(false);
      setIsGreenAlertManuallyEdited(false);
      setIsSubcontractedAlertManuallyEdited(false);
      setIsStorageAlertManuallyEdited(false);
      setSubcontractedAlertOverrides({});
      setIsSiteBudgetFocused(false);
      return;
    }
    const baseForm = buildFormState(selectedLocation.location_id);
    setFormValues(baseForm);
    setInitialFormValues(baseForm);
    setNewEmail('');
    setIsPartsAlertManuallyEdited(hasCustomAlert(baseForm, 'parts_budget', 'parts_alert_threshold'));
    setIsGenuineAlertManuallyEdited(
      hasCustomAlert(baseForm, 'genuine_parts_budget', 'genuine_parts_alert_threshold')
    );
    setIsNonGenuineAlertManuallyEdited(
      hasCustomAlert(baseForm, 'non_genuine_parts_budget', 'non_genuine_parts_alert_threshold')
    );
    setIsGreenAlertManuallyEdited(
      hasCustomAlert(baseForm, 'green_parts_budget', 'green_parts_alert_threshold')
    );
    setIsSubcontractedAlertManuallyEdited(
      hasCustomAlert(baseForm, 'subcontracted_budget', 'subcontracted_alert_threshold')
    );
    const overrides = {};
    subcontractedFieldDefs.forEach((def) => {
      overrides[def.alertField] = hasCustomAlert(baseForm, def.budgetField, def.alertField);
    });
    setSubcontractedAlertOverrides(overrides);
    setIsStorageAlertManuallyEdited(
      hasCustomAlert(baseForm, 'storage_recovery_budget', 'storage_recovery_alert_threshold')
    );
    setIsSiteBudgetFocused(false);
  }, [selectedLocation, buildFormState, subcontractedFieldDefs]);

  const handleSiteBudgetChange = (value) => {
    const sanitized = value.replace(/,/g, '');
    if (sanitized === '' || siteBudgetPattern.test(sanitized)) {
      setFormValues((prev) => ({ ...prev, site_budget: sanitized }));
    }
  };

  const handlePartsBudgetChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      const allowed = value === '' || percentPattern.test(value);
      if (!allowed) return prev;

      const numeric = value === '' ? null : Number(value);
      if (numeric !== null && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
        return prev;
      }

      const next = { ...prev, parts_budget: value };
      const alertNumeric = Number(prev.parts_alert_threshold);

      if (value === '') {
        if (prev.parts_alert_threshold !== '') {
          next.parts_alert_threshold = '';
        }
        manualOverride = false;
        return next;
      }

      const shouldSyncAlert =
        !isPartsAlertManuallyEdited ||
        prev.parts_alert_threshold === '' ||
        Number.isNaN(alertNumeric) ||
        alertNumeric > numeric;

      if (shouldSyncAlert) {
        next.parts_alert_threshold = value;
        manualOverride = false;
      } else if (!Number.isNaN(alertNumeric) && alertNumeric > numeric) {
        next.parts_alert_threshold = value;
        manualOverride = false;
      }

      return next;
    });
    if (manualOverride !== null) {
      setIsPartsAlertManuallyEdited(manualOverride);
    }
  };

  const handlePartsAlertChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      if (value === '') {
        manualOverride = false;
        return { ...prev, parts_alert_threshold: value };
      }

      if (!percentPattern.test(value)) {
        return prev;
      }

      const numeric = Number(value);
      const budgetNumeric = Number(prev.parts_budget);
      if (
        Number.isNaN(numeric) ||
        numeric < 0 ||
        numeric > 100 ||
        (!Number.isNaN(budgetNumeric) && numeric > budgetNumeric)
      ) {
        return prev;
      }
      manualOverride = true;
      return { ...prev, parts_alert_threshold: value };
    });
    if (manualOverride !== null) {
      setIsPartsAlertManuallyEdited(manualOverride);
    }
  };

  const handleGenuineBudgetChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      const allowed = value === '' || percentPattern.test(value);
      if (!allowed) return prev;

      const numeric = value === '' ? null : Number(value);
      if (numeric !== null && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
        return prev;
      }

      const next = { ...prev, genuine_parts_budget: value };
      const alertNumeric = Number(prev.genuine_parts_alert_threshold);

      if (value === '') {
        if (prev.genuine_parts_alert_threshold !== '') {
          next.genuine_parts_alert_threshold = '';
        }
        manualOverride = false;
        return next;
      }

      const shouldSyncAlert =
        !isGenuineAlertManuallyEdited ||
        prev.genuine_parts_alert_threshold === '' ||
        Number.isNaN(alertNumeric) ||
        alertNumeric > numeric;

      if (shouldSyncAlert) {
        next.genuine_parts_alert_threshold = value;
        manualOverride = false;
      } else if (!Number.isNaN(alertNumeric) && alertNumeric > numeric) {
        next.genuine_parts_alert_threshold = value;
        manualOverride = false;
      }

      return next;
    });
    if (manualOverride !== null) {
      setIsGenuineAlertManuallyEdited(manualOverride);
    }
  };

  const handleGenuineAlertChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      if (value === '') {
        manualOverride = false;
        return { ...prev, genuine_parts_alert_threshold: value };
      }

      if (!percentPattern.test(value)) {
        return prev;
      }

      const numeric = Number(value);
      const budgetNumeric = Number(prev.genuine_parts_budget);
      if (
        Number.isNaN(numeric) ||
        numeric < 0 ||
        numeric > 100 ||
        (!Number.isNaN(budgetNumeric) && numeric > budgetNumeric)
      ) {
        return prev;
      }
      manualOverride = true;
      return { ...prev, genuine_parts_alert_threshold: value };
    });
    if (manualOverride !== null) {
      setIsGenuineAlertManuallyEdited(manualOverride);
    }
  };

  const handleNonGenuineBudgetChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      const allowed = value === '' || percentPattern.test(value);
      if (!allowed) return prev;

      const numeric = value === '' ? null : Number(value);
      if (numeric !== null && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
        return prev;
      }

      const next = { ...prev, non_genuine_parts_budget: value };
      const alertNumeric = Number(prev.non_genuine_parts_alert_threshold);

      if (value === '') {
        if (prev.non_genuine_parts_alert_threshold !== '') {
          next.non_genuine_parts_alert_threshold = '';
        }
        manualOverride = false;
        return next;
      }

      const shouldSyncAlert =
        !isNonGenuineAlertManuallyEdited ||
        prev.non_genuine_parts_alert_threshold === '' ||
        Number.isNaN(alertNumeric) ||
        alertNumeric > numeric;

      if (shouldSyncAlert) {
        next.non_genuine_parts_alert_threshold = value;
        manualOverride = false;
      } else if (!Number.isNaN(alertNumeric) && alertNumeric > numeric) {
        next.non_genuine_parts_alert_threshold = value;
        manualOverride = false;
      }

      return next;
    });
    if (manualOverride !== null) {
      setIsNonGenuineAlertManuallyEdited(manualOverride);
    }
  };

  const handleNonGenuineAlertChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      if (value === '') {
        manualOverride = false;
        return { ...prev, non_genuine_parts_alert_threshold: value };
      }

      if (!percentPattern.test(value)) {
        return prev;
      }

      const numeric = Number(value);
      const budgetNumeric = Number(prev.non_genuine_parts_budget);
      if (
        Number.isNaN(numeric) ||
        numeric < 0 ||
        numeric > 100 ||
        (!Number.isNaN(budgetNumeric) && numeric > budgetNumeric)
      ) {
        return prev;
      }
      manualOverride = true;
      return { ...prev, non_genuine_parts_alert_threshold: value };
    });
    if (manualOverride !== null) {
      setIsNonGenuineAlertManuallyEdited(manualOverride);
    }
  };

  const handleGreenBudgetChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      const allowed = value === '' || percentPattern.test(value);
      if (!allowed) return prev;

      const numeric = value === '' ? null : Number(value);
      if (numeric !== null && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
        return prev;
      }

      const next = { ...prev, green_parts_budget: value };
      const alertNumeric = Number(prev.green_parts_alert_threshold);

      if (value === '') {
        if (prev.green_parts_alert_threshold !== '') {
          next.green_parts_alert_threshold = '';
        }
        manualOverride = false;
        return next;
      }

      const shouldSyncAlert =
        !isGreenAlertManuallyEdited ||
        prev.green_parts_alert_threshold === '' ||
        Number.isNaN(alertNumeric) ||
        alertNumeric > numeric;

      if (shouldSyncAlert) {
        next.green_parts_alert_threshold = value;
        manualOverride = false;
      } else if (!Number.isNaN(alertNumeric) && alertNumeric > numeric) {
        next.green_parts_alert_threshold = value;
        manualOverride = false;
      }

      return next;
    });
    if (manualOverride !== null) {
      setIsGreenAlertManuallyEdited(manualOverride);
    }
  };

  const handleGreenAlertChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      if (value === '') {
        manualOverride = false;
        return { ...prev, green_parts_alert_threshold: value };
      }

      if (!percentPattern.test(value)) {
        return prev;
      }

      const numeric = Number(value);
      const budgetNumeric = Number(prev.green_parts_budget);
      if (
        Number.isNaN(numeric) ||
        numeric < 0 ||
        numeric > 100 ||
        (!Number.isNaN(budgetNumeric) && numeric > budgetNumeric)
      ) {
        return prev;
      }
      manualOverride = true;
      return { ...prev, green_parts_alert_threshold: value };
    });
    if (manualOverride !== null) {
      setIsGreenAlertManuallyEdited(manualOverride);
    }
  };

  const handleSubcontractedBudgetChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      const allowed = value === '' || percentPattern.test(value);
      if (!allowed) return prev;

      const numeric = value === '' ? null : Number(value);
      if (numeric !== null && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
        return prev;
      }

      const next = { ...prev, subcontracted_budget: value };
      const alertNumeric = Number(prev.subcontracted_alert_threshold);

      if (value === '') {
        if (prev.subcontracted_alert_threshold !== '') {
          next.subcontracted_alert_threshold = '';
        }
        manualOverride = false;
        return next;
      }

      const shouldSyncAlert =
        !isSubcontractedAlertManuallyEdited ||
        prev.subcontracted_alert_threshold === '' ||
        Number.isNaN(alertNumeric) ||
        alertNumeric > numeric;

      if (shouldSyncAlert) {
        next.subcontracted_alert_threshold = value;
        manualOverride = false;
      } else if (!Number.isNaN(alertNumeric) && alertNumeric > numeric) {
        next.subcontracted_alert_threshold = value;
        manualOverride = false;
      }

      return next;
    });
    if (manualOverride !== null) {
      setIsSubcontractedAlertManuallyEdited(manualOverride);
    }
  };

  const handleSubcontractedAlertChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      if (value === '') {
        manualOverride = false;
        return { ...prev, subcontracted_alert_threshold: value };
      }

      if (!percentPattern.test(value)) {
        return prev;
      }

      const numeric = Number(value);
      const budgetNumeric = Number(prev.subcontracted_budget);
      if (
        Number.isNaN(numeric) ||
        numeric < 0 ||
        numeric > 100 ||
        (!Number.isNaN(budgetNumeric) && numeric > budgetNumeric)
      ) {
        return prev;
      }
      manualOverride = true;
      return { ...prev, subcontracted_alert_threshold: value };
    });
    if (manualOverride !== null) {
      setIsSubcontractedAlertManuallyEdited(manualOverride);
    }
  };

  const handleSubcontractedChildBudgetChange = (def, value) => {
    let manualOverride = null;
    const isManual = Boolean(subcontractedAlertOverrides?.[def.alertField]);
    setFormValues((prev) => {
      const allowed = value === '' || percentPattern.test(value);
      if (!allowed) return prev;

      const numeric = value === '' ? null : Number(value);
      if (numeric !== null && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
        return prev;
      }

      const next = { ...prev, [def.budgetField]: value };
      const alertNumeric = Number(prev[def.alertField]);

      if (value === '') {
        if (prev[def.alertField] !== '') {
          next[def.alertField] = '';
        }
        manualOverride = false;
        return next;
      }

      const shouldSyncAlert =
        !isManual ||
        prev[def.alertField] === '' ||
        Number.isNaN(alertNumeric) ||
        alertNumeric > numeric;

      if (shouldSyncAlert) {
        next[def.alertField] = value;
        manualOverride = false;
      } else if (!Number.isNaN(alertNumeric) && alertNumeric > numeric) {
        next[def.alertField] = value;
        manualOverride = false;
      }

      return next;
    });
    if (manualOverride !== null) {
      setSubcontractedAlertOverrides((prev) => ({
        ...prev,
        [def.alertField]: manualOverride,
      }));
    }
  };

  const handleSubcontractedChildAlertChange = (def, value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      if (value === '') {
        manualOverride = false;
        return { ...prev, [def.alertField]: value };
      }

      if (!percentPattern.test(value)) {
        return prev;
      }

      const numeric = Number(value);
      const budgetNumeric = Number(prev[def.budgetField]);
      if (
        Number.isNaN(numeric) ||
        numeric < 0 ||
        numeric > 100 ||
        (!Number.isNaN(budgetNumeric) && numeric > budgetNumeric)
      ) {
        return prev;
      }
      manualOverride = true;
      return { ...prev, [def.alertField]: value };
    });
    if (manualOverride !== null) {
      setSubcontractedAlertOverrides((prev) => ({
        ...prev,
        [def.alertField]: manualOverride,
      }));
    }
  };

  const handleStorageBudgetChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      const allowed = value === '' || percentPattern.test(value);
      if (!allowed) return prev;

      const numeric = value === '' ? null : Number(value);
      if (numeric !== null && (Number.isNaN(numeric) || numeric < 0 || numeric > 100)) {
        return prev;
      }

      const next = { ...prev, storage_recovery_budget: value };
      const alertNumeric = Number(prev.storage_recovery_alert_threshold);

      if (value === '') {
        if (prev.storage_recovery_alert_threshold !== '') {
          next.storage_recovery_alert_threshold = '';
        }
        manualOverride = false;
        return next;
      }

      const shouldSyncAlert =
        !isStorageAlertManuallyEdited ||
        prev.storage_recovery_alert_threshold === '' ||
        Number.isNaN(alertNumeric) ||
        alertNumeric > numeric;

      if (shouldSyncAlert) {
        next.storage_recovery_alert_threshold = value;
        manualOverride = false;
      } else if (!Number.isNaN(alertNumeric) && alertNumeric > numeric) {
        next.storage_recovery_alert_threshold = value;
        manualOverride = false;
      }

      return next;
    });
    if (manualOverride !== null) {
      setIsStorageAlertManuallyEdited(manualOverride);
    }
  };

  const handleStorageAlertChange = (value) => {
    let manualOverride = null;
    setFormValues((prev) => {
      if (value === '') {
        manualOverride = false;
        return { ...prev, storage_recovery_alert_threshold: value };
      }

      if (!percentPattern.test(value)) {
        return prev;
      }

      const numeric = Number(value);
      const budgetNumeric = Number(prev.storage_recovery_budget);
      if (
        Number.isNaN(numeric) ||
        numeric < 0 ||
        numeric > 100 ||
        (!Number.isNaN(budgetNumeric) && numeric > budgetNumeric)
      ) {
        return prev;
      }
      manualOverride = true;
      return { ...prev, storage_recovery_alert_threshold: value };
    });
    if (manualOverride !== null) {
      setIsStorageAlertManuallyEdited(manualOverride);
    }
  };

  const handleAddEmail = () => {
    if (readOnly) return;
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;
    if (!emailPattern.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    if ((formValues.notify_users || []).includes(trimmed)) {
      setNewEmail('');
      return;
    }
    setFormValues((prev) => ({ ...prev, notify_users: [...(prev.notify_users || []), trimmed] }));
    setNewEmail('');
    setError('');
  };

  const handleEmailKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddEmail();
    }
  };

  const handleRemoveEmail = (email) => {
    if (readOnly) return;
    setFormValues((prev) => ({
      ...prev,
      notify_users: (prev.notify_users || []).filter((item) => item !== email),
    }));
  };

  const parseSiteBudgetValue = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return null;
    }
    if (numericValue < 0 || numericValue > 999999.99) {
      return null;
    }
    return Math.round(numericValue * 100) / 100;
  };

  const parsePercentValue = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return null;
    }
    if (numericValue < 0 || numericValue > 100) {
      return null;
    }
    return Math.round(numericValue * 100) / 100;
  };

  const isFormDirty = useMemo(() => {
    const compareFields = [
      'site_budget',
      'parts_budget',
      'parts_alert_threshold',
      'genuine_parts_budget',
      'genuine_parts_alert_threshold',
      'non_genuine_parts_budget',
      'non_genuine_parts_alert_threshold',
      'green_parts_budget',
      'green_parts_alert_threshold',
      'subcontracted_budget',
      'subcontracted_alert_threshold',
      'storage_recovery_budget',
      'storage_recovery_alert_threshold',
    ];
    subcontractedFieldDefs.forEach((def) => {
      compareFields.push(def.budgetField);
      compareFields.push(def.alertField);
    });
    for (const field of compareFields) {
      if ((formValues[field] || '') !== (initialFormValues[field] || '')) {
        return true;
      }
    }
    const currentList = normaliseStringArray(formValues.notify_users);
    const initialList = normaliseStringArray(initialFormValues.notify_users);
    if (currentList.length !== initialList.length) {
      return true;
    }
    for (let i = 0; i < currentList.length; i += 1) {
      if (currentList[i] !== initialList[i]) {
        return true;
      }
    }
    return false;
  }, [formValues, initialFormValues]);

  const budgetSum = useMemo(() => {
    const partsValue = parsePercentValue(formValues.parts_budget);
    const subcontractedValue = parsePercentValue(formValues.subcontracted_budget);
    const storageValue = parsePercentValue(formValues.storage_recovery_budget);
    const excludedSubcontractedSum = excludedSubcontractedDefs.reduce((acc, def) => {
      if (acc === null) return null;
      const value = parsePercentValue(formValues[def.budgetField]);
      if (value === null) {
        return null;
      }
      return acc + value;
    }, 0);
    if (
      partsValue === null ||
      subcontractedValue === null ||
      excludedSubcontractedSum === null ||
      storageValue === null
    ) {
      return null;
    }
    return partsValue + subcontractedValue + excludedSubcontractedSum + storageValue;
  }, [
    formValues.parts_budget,
    formValues.subcontracted_budget,
    formValues.storage_recovery_budget,
    formValues,
    excludedSubcontractedDefs,
  ]);

  const isBudgetSumInvalid = budgetSum !== null && budgetSum > 100 + 1e-6;

  const partsSplitSum = useMemo(() => {
    const genuineValue = parsePercentValue(formValues.genuine_parts_budget);
    const nonGenuineValue = parsePercentValue(formValues.non_genuine_parts_budget);
    const greenValue = parsePercentValue(formValues.green_parts_budget);
    if (genuineValue === null || nonGenuineValue === null || greenValue === null) {
      return null;
    }
    return genuineValue + nonGenuineValue + greenValue;
  }, [
    formValues.genuine_parts_budget,
    formValues.non_genuine_parts_budget,
    formValues.green_parts_budget,
  ]);

  const isPartsSplitInvalid = partsSplitSum !== null && Math.abs(partsSplitSum - 100) > 1e-6;

  const handleSave = async () => {
    if (!selectedLocation) {
      setError('Please select a location.');
      return;
    }
    if (!selectedBudgetPeriod) {
      setError('Please select a budget period.');
      return;
    }
    const siteBudgetValue = parseSiteBudgetValue(formValues.site_budget);
    const partsBudgetValue = parsePercentValue(formValues.parts_budget);
    const partsAlertValue = parsePercentValue(formValues.parts_alert_threshold);
    const genuineBudgetValue = parsePercentValue(formValues.genuine_parts_budget);
    const genuineAlertValue = parsePercentValue(formValues.genuine_parts_alert_threshold);
    const nonGenuineBudgetValue = parsePercentValue(formValues.non_genuine_parts_budget);
    const nonGenuineAlertValue = parsePercentValue(formValues.non_genuine_parts_alert_threshold);
    const greenBudgetValue = parsePercentValue(formValues.green_parts_budget);
    const greenAlertValue = parsePercentValue(formValues.green_parts_alert_threshold);
    const subcontractedBudgetValue = parsePercentValue(formValues.subcontracted_budget);
    const subcontractedAlertValue = parsePercentValue(formValues.subcontracted_alert_threshold);
    const excludedChildValues = {};
    for (const def of excludedSubcontractedDefs) {
      const budgetValue = parsePercentValue(formValues[def.budgetField]);
      const alertValue = parsePercentValue(formValues[def.alertField]);
      if (budgetValue === null) {
        setError(`${def.label} budget percentage is required.`);
        return;
      }
      if (alertValue === null) {
        setError(`${def.label} alert threshold percentage is required.`);
        return;
      }
      excludedChildValues[def.budgetField] = budgetValue;
      excludedChildValues[def.alertField] = alertValue;
    }
    const storageBudgetValue = parsePercentValue(formValues.storage_recovery_budget);
    const storageAlertValue = parsePercentValue(formValues.storage_recovery_alert_threshold);

    if (siteBudgetValue === null) {
      setError('Site budget is required.');
      return;
    }

    if (partsBudgetValue === null) {
      setError('Parts budget percentage is required.');
      return;
    }

    if (partsAlertValue === null) {
      setError('Parts alert threshold percentage is required.');
      return;
    }

    if (genuineBudgetValue === null) {
      setError('Genuine parts budget percentage is required.');
      return;
    }

    if (genuineAlertValue === null) {
      setError('Genuine parts alert threshold percentage is required.');
      return;
    }

    if (nonGenuineBudgetValue === null) {
      setError('Non genuine parts budget percentage is required.');
      return;
    }

    if (nonGenuineAlertValue === null) {
      setError('Non genuine parts alert threshold percentage is required.');
      return;
    }

    if (greenBudgetValue === null) {
      setError('Green parts budget percentage is required.');
      return;
    }

    if (greenAlertValue === null) {
      setError('Green parts alert threshold percentage is required.');
      return;
    }

    if (Math.abs(genuineBudgetValue + nonGenuineBudgetValue + greenBudgetValue - 100) > 1e-6) {
      setError('Genuine, non genuine, and green parts budgets must total 100%.');
      return;
    }

    if (subcontractedBudgetValue === null) {
      setError('Subcontracted budget percentage is required.');
      return;
    }

    if (subcontractedAlertValue === null) {
      setError('Subcontracted alert threshold percentage is required.');
      return;
    }

    if (storageBudgetValue === null) {
      setError('Storage & recovery budget percentage is required.');
      return;
    }

    if (storageAlertValue === null) {
      setError('Storage & recovery alert threshold percentage is required.');
      return;
    }

    const excludedChildBudgetTotal = excludedSubcontractedDefs.reduce(
      (acc, def) => acc + (excludedChildValues[def.budgetField] || 0),
      0
    );
    if (partsBudgetValue + subcontractedBudgetValue + excludedChildBudgetTotal + storageBudgetValue > 100) {
      setError('Combined parts, subcontracted, excluded subcontracted categories, and storage & recovery budgets must not exceed 100%.');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      location_id: selectedLocation.location_id,
      budget_month: selectedBudgetPeriod.month,
      budget_year: selectedBudgetPeriod.year,
      site_budget: siteBudgetValue,
      parts_budget: partsBudgetValue,
      parts_alert_threshold: partsAlertValue,
      genuine_parts_budget: genuineBudgetValue,
      genuine_parts_alert_threshold: genuineAlertValue,
      non_genuine_parts_budget: nonGenuineBudgetValue,
      non_genuine_parts_alert_threshold: nonGenuineAlertValue,
      green_parts_budget: greenBudgetValue,
      green_parts_alert_threshold: greenAlertValue,
      subcontracted_budget: subcontractedBudgetValue,
      subcontracted_alert_threshold: subcontractedAlertValue,
      ...subcontractedFieldDefs.reduce((acc, def) => {
        const value = excludedChildValues[def.budgetField];
        const alert = excludedChildValues[def.alertField];
        const fallbackBudget = parsePercentValue(formValues[def.budgetField]);
        const fallbackAlert = parsePercentValue(formValues[def.alertField]);
        acc[def.budgetField] = value !== undefined ? value : fallbackBudget ?? 0;
        acc[def.alertField] = alert !== undefined ? alert : fallbackAlert ?? 0;
        return acc;
      }, {}),
      storage_recovery_budget: storageBudgetValue,
      storage_recovery_alert_threshold: storageAlertValue,
      notify_users: formValues.notify_users || [],
    };

    try {
      const isExisting = Boolean(formValues.budgetId);
      const response = isExisting
        ? await axiosInstance.put(`/api/v1/budget/${formValues.budgetId}`, payload)
        : await axiosInstance.post('/api/v1/budget', payload);

      const updatedBudget = response?.data?.data;
      if (!updatedBudget) {
        throw new Error('Unexpected response from server.');
      }

      setBudgets((prev) => {
        const next = [...prev];
        const index = next.findIndex((item) => item.id === updatedBudget.id);
        if (index > -1) {
          next[index] = updatedBudget;
          return next;
        }
        return [...next, updatedBudget];
      });
      const nextState = {
        budgetId: updatedBudget.id || null,
        site_budget:
          updatedBudget.site_budget !== undefined && updatedBudget.site_budget !== null
            ? String(updatedBudget.site_budget)
            : '',
        parts_budget: normalizePercentValue(updatedBudget.parts_budget),
        parts_alert_threshold: normalizePercentValue(updatedBudget.parts_alert_threshold),
        genuine_parts_budget: normalizePercentValue(updatedBudget.genuine_parts_budget),
        genuine_parts_alert_threshold: normalizePercentValue(updatedBudget.genuine_parts_alert_threshold),
        non_genuine_parts_budget: normalizePercentValue(updatedBudget.non_genuine_parts_budget),
        non_genuine_parts_alert_threshold: normalizePercentValue(updatedBudget.non_genuine_parts_alert_threshold),
        green_parts_budget: normalizePercentValue(updatedBudget.green_parts_budget),
        green_parts_alert_threshold: normalizePercentValue(updatedBudget.green_parts_alert_threshold),
        subcontracted_budget: normalizePercentValue(updatedBudget.subcontracted_budget),
        subcontracted_alert_threshold: normalizePercentValue(updatedBudget.subcontracted_alert_threshold),
        subcontracted_adas_calibration_diagnostics_budget: normalizePercentValue(updatedBudget.subcontracted_adas_calibration_diagnostics_budget),
        subcontracted_adas_calibration_diagnostics_alert_threshold: normalizePercentValue(
          updatedBudget.subcontracted_adas_calibration_diagnostics_alert_threshold
        ),
        subcontracted_general_budget: normalizePercentValue(updatedBudget.subcontracted_general_budget),
        subcontracted_general_alert_threshold: normalizePercentValue(updatedBudget.subcontracted_general_alert_threshold),
        subcontracted_lock_set_budget: normalizePercentValue(updatedBudget.subcontracted_lock_set_budget),
        subcontracted_lock_set_alert_threshold: normalizePercentValue(updatedBudget.subcontracted_lock_set_alert_threshold),
        subcontracted_signwriting_graphics_budget: normalizePercentValue(updatedBudget.subcontracted_signwriting_graphics_budget),
        subcontracted_signwriting_graphics_alert_threshold: normalizePercentValue(
          updatedBudget.subcontracted_signwriting_graphics_alert_threshold
        ),
        subcontracted_tyres_alloy_wheels_budget: normalizePercentValue(updatedBudget.subcontracted_tyres_alloy_wheels_budget),
        subcontracted_tyres_alloy_wheels_alert_threshold: normalizePercentValue(
          updatedBudget.subcontracted_tyres_alloy_wheels_alert_threshold
        ),
        subcontracted_windscreen_repairs_budget: normalizePercentValue(updatedBudget.subcontracted_windscreen_repairs_budget),
        subcontracted_windscreen_repairs_alert_threshold: normalizePercentValue(
          updatedBudget.subcontracted_windscreen_repairs_alert_threshold
        ),
        storage_recovery_budget: normalizePercentValue(updatedBudget.storage_recovery_budget),
        storage_recovery_alert_threshold: normalizePercentValue(updatedBudget.storage_recovery_alert_threshold),
        notify_users: Array.isArray(updatedBudget.notify_users) ? updatedBudget.notify_users : [],
        created_on: updatedBudget.created_on || '',
        created_by: updatedBudget.created_by || '',
        updated_on: updatedBudget.updated_on || '',
        updated_by: updatedBudget.updated_by || '',
      };
      setFormValues(nextState);
      setInitialFormValues(nextState);
      setIsPartsAlertManuallyEdited(hasCustomAlert(nextState, 'parts_budget', 'parts_alert_threshold'));
      setIsGenuineAlertManuallyEdited(
        hasCustomAlert(nextState, 'genuine_parts_budget', 'genuine_parts_alert_threshold')
      );
      setIsNonGenuineAlertManuallyEdited(
        hasCustomAlert(nextState, 'non_genuine_parts_budget', 'non_genuine_parts_alert_threshold')
      );
      setIsGreenAlertManuallyEdited(
        hasCustomAlert(nextState, 'green_parts_budget', 'green_parts_alert_threshold')
      );
      setIsSubcontractedAlertManuallyEdited(
        hasCustomAlert(nextState, 'subcontracted_budget', 'subcontracted_alert_threshold')
      );
      const updatedOverrides = {};
      subcontractedFieldDefs.forEach((def) => {
        updatedOverrides[def.alertField] = hasCustomAlert(nextState, def.budgetField, def.alertField);
      });
      setSubcontractedAlertOverrides(updatedOverrides);
      setIsStorageAlertManuallyEdited(
        hasCustomAlert(nextState, 'storage_recovery_budget', 'storage_recovery_alert_threshold')
      );
      setSuccess('Budget saved successfully.');
    } catch (err) {
      const message = err?.response?.data?.error || 'Unable to save budget.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (readOnly) return;
    if (!formValues?.budgetId) {
      return;
    }

    setConfirmDialog({
      open: true,
      action: 'delete-location',
      title: 'Delete Budget',
      message: 'Are you sure you want to delete this budget configuration?',
    });
  };

  const handleApplyAll = () => {
    if (readOnly) return;
    if (!selectedBudgetPeriod) {
      setError('Please select a budget period.');
      return;
    }
    const siteBudgetValue = parseSiteBudgetValue(formValues.site_budget);
    const partsBudgetValue = parsePercentValue(formValues.parts_budget);
    const partsAlertValue = parsePercentValue(formValues.parts_alert_threshold);
    const genuineBudgetValue = parsePercentValue(formValues.genuine_parts_budget);
    const genuineAlertValue = parsePercentValue(formValues.genuine_parts_alert_threshold);
    const nonGenuineBudgetValue = parsePercentValue(formValues.non_genuine_parts_budget);
    const nonGenuineAlertValue = parsePercentValue(formValues.non_genuine_parts_alert_threshold);
    const greenBudgetValue = parsePercentValue(formValues.green_parts_budget);
    const greenAlertValue = parsePercentValue(formValues.green_parts_alert_threshold);
    const subcontractedBudgetValue = parsePercentValue(formValues.subcontracted_budget);
    const subcontractedAlertValue = parsePercentValue(formValues.subcontracted_alert_threshold);
    const excludedChildValues = {};
    for (const def of excludedSubcontractedDefs) {
      const budgetValue = parsePercentValue(formValues[def.budgetField]);
      const alertValue = parsePercentValue(formValues[def.alertField]);
      if (budgetValue === null || alertValue === null) {
        setError('Set all budget values before applying to all locations.');
        return;
      }
      excludedChildValues[def.budgetField] = budgetValue;
      excludedChildValues[def.alertField] = alertValue;
    }
    const storageBudgetValue = parsePercentValue(formValues.storage_recovery_budget);
    const storageAlertValue = parsePercentValue(formValues.storage_recovery_alert_threshold);

    if (
      siteBudgetValue === null ||
      partsBudgetValue === null ||
      partsAlertValue === null ||
      genuineBudgetValue === null ||
      genuineAlertValue === null ||
      nonGenuineBudgetValue === null ||
      nonGenuineAlertValue === null ||
      greenBudgetValue === null ||
      greenAlertValue === null ||
      subcontractedBudgetValue === null ||
      subcontractedAlertValue === null ||
      storageBudgetValue === null ||
      storageAlertValue === null
    ) {
      setError('Set all budget values before applying to all locations.');
      return;
    }

    if (Math.abs(genuineBudgetValue + nonGenuineBudgetValue + greenBudgetValue - 100) > 1e-6) {
      setError('Genuine, non genuine, and green parts budgets must total 100%.');
      return;
    }

    const excludedChildBudgetTotal = excludedSubcontractedDefs.reduce(
      (acc, def) => acc + (excludedChildValues[def.budgetField] || 0),
      0
    );
    if (partsBudgetValue + subcontractedBudgetValue + excludedChildBudgetTotal + storageBudgetValue > 100) {
      setError('Combined parts, subcontracted, excluded subcontracted categories, and storage & recovery budgets must not exceed 100%.');
      return;
    }

    setConfirmDialog({
      open: true,
      action: 'apply-all',
      title: 'Apply To All Locations',
      message: 'Apply these budget settings to all locations? This will overwrite existing values.',
    });
  };

  const handleCopyPreviousMonthForLocation = () => {
    if (readOnly) return;
    if (!selectedBudgetPeriod) {
      setError('Please select a budget period.');
      return;
    }
    if (!selectedLocation) {
      setError('Please select a location.');
      return;
    }
    if (!previousBudgetPeriod) {
      setError('Unable to determine the previous budget period.');
      return;
    }
    if (!selectedLocationPreviousBudget) {
      setError(`No budget found for ${selectedLocation.name} in ${previousBudgetPeriod.label}.`);
      return;
    }

    setConfirmDialog({
      open: true,
      action: 'copy-previous-location',
      title: 'Copy Previous Month',
      message: `Copy ${previousBudgetPeriod.label} budget into ${selectedBudgetPeriod.label} for ${selectedLocation.name}?`,
    });
  };

  const handleCopyPreviousMonthForAllLocations = () => {
    if (readOnly) return;
    if (!selectedBudgetPeriod) {
      setError('Please select a budget period.');
      return;
    }
    if (!previousBudgetPeriod) {
      setError('Unable to determine the previous budget period.');
      return;
    }
    if (availablePreviousMonthBudgets.length === 0) {
      setError(`No budgets found for ${previousBudgetPeriod.label}.`);
      return;
    }

    setConfirmDialog({
      open: true,
      action: 'copy-previous-all',
      title: 'Copy Previous Month To All Sites',
      message: `Copy all available ${previousBudgetPeriod.label} budgets into ${selectedBudgetPeriod.label}? Existing budgets for the selected month will be overwritten.`,
    });
  };

  const handleBudgetFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (readOnly) {
      if (budgetFileInputRef.current) budgetFileInputRef.current.value = '';
      return;
    }

    if (!selectedBudgetPeriod) {
      setError('Please select a budget period before uploading a budget file.');
      if (budgetFileInputRef.current) budgetFileInputRef.current.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('budget_month', String(selectedBudgetPeriod.month));
    formData.append('budget_year', String(selectedBudgetPeriod.year));

    setUploadingBudget(true);
    setError('');
    try {
      const response = await axiosInstance.post('/api/v1/budget/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const meta = response?.data?.meta || {};
      await fetchData();
      const skippedText = meta.skipped ? `, ${meta.skipped} skipped` : '';
      setSuccess(
        `Budget file uploaded for ${selectedBudgetPeriod.label}: ${meta.rows_processed || 0} rows processed (${meta.created || 0} created, ${meta.updated || 0} updated${skippedText}).`
      );
    } catch (err) {
      const message = err?.response?.data?.error || 'Unable to upload budget file.';
      setError(message);
    } finally {
      setUploadingBudget(false);
      if (budgetFileInputRef.current) budgetFileInputRef.current.value = '';
    }
  };

  const executeConfirmedAction = async () => {
    if (readOnly) {
      setConfirmDialog({ open: false, action: null, title: '', message: '' });
      return;
    }
    if (confirmDialog.action === 'delete-location') {
      if (!formValues?.budgetId) {
        setConfirmDialog({ open: false, action: null, title: '', message: '' });
        return;
      }

      setSaving(true);
      setError('');
      try {
        await axiosInstance.delete(`/api/v1/budget/${formValues.budgetId}`);
        setBudgets((prev) => prev.filter((budget) => budget.id !== formValues.budgetId));
        setSuccess('Budget deleted successfully.');
        setFormValues(createEmptyForm());
        setInitialFormValues(createEmptyForm());
        setIsPartsAlertManuallyEdited(false);
        setIsGenuineAlertManuallyEdited(false);
        setIsNonGenuineAlertManuallyEdited(false);
        setIsGreenAlertManuallyEdited(false);
        setIsSubcontractedAlertManuallyEdited(false);
        setIsStorageAlertManuallyEdited(false);
        setSubcontractedAlertOverrides({});
      } catch (err) {
        const message = err?.response?.data?.error || 'Unable to delete budget.';
        setError(message);
      } finally {
        setSaving(false);
      }
    } else if (confirmDialog.action === 'apply-all') {
      if (!selectedBudgetPeriod) {
        setError('Please select a budget period.');
        return;
      }
      const siteBudgetValue = parseSiteBudgetValue(formValues.site_budget);
      const partsBudgetValue = parsePercentValue(formValues.parts_budget);
      const partsAlertValue = parsePercentValue(formValues.parts_alert_threshold);
      const genuineBudgetValue = parsePercentValue(formValues.genuine_parts_budget);
      const genuineAlertValue = parsePercentValue(formValues.genuine_parts_alert_threshold);
      const nonGenuineBudgetValue = parsePercentValue(formValues.non_genuine_parts_budget);
      const nonGenuineAlertValue = parsePercentValue(formValues.non_genuine_parts_alert_threshold);
      const greenBudgetValue = parsePercentValue(formValues.green_parts_budget);
      const greenAlertValue = parsePercentValue(formValues.green_parts_alert_threshold);
      const subcontractedBudgetValue = parsePercentValue(formValues.subcontracted_budget);
      const subcontractedAlertValue = parsePercentValue(formValues.subcontracted_alert_threshold);
      const excludedChildValues = {};
      for (const def of excludedSubcontractedDefs) {
        const budgetValue = parsePercentValue(formValues[def.budgetField]);
        const alertValue = parsePercentValue(formValues[def.alertField]);
        if (budgetValue === null || alertValue === null) {
          setError('Set all budget values before applying to all locations.');
          return;
        }
        excludedChildValues[def.budgetField] = budgetValue;
        excludedChildValues[def.alertField] = alertValue;
      }
      const storageBudgetValue = parsePercentValue(formValues.storage_recovery_budget);
      const storageAlertValue = parsePercentValue(formValues.storage_recovery_alert_threshold);

      setSaving(true);
      setError('');
      try {
        await Promise.all(
          locations.map((location) => {
            const payload = {
              location_id: location.location_id,
              budget_month: selectedBudgetPeriod.month,
              budget_year: selectedBudgetPeriod.year,
              site_budget: siteBudgetValue,
              parts_budget: partsBudgetValue,
              parts_alert_threshold: partsAlertValue,
              genuine_parts_budget: genuineBudgetValue,
              genuine_parts_alert_threshold: genuineAlertValue,
              non_genuine_parts_budget: nonGenuineBudgetValue,
              non_genuine_parts_alert_threshold: nonGenuineAlertValue,
              green_parts_budget: greenBudgetValue,
              green_parts_alert_threshold: greenAlertValue,
              subcontracted_budget: subcontractedBudgetValue,
              subcontracted_alert_threshold: subcontractedAlertValue,
              ...subcontractedFieldDefs.reduce((acc, def) => {
                const value = excludedChildValues[def.budgetField];
                const alert = excludedChildValues[def.alertField];
                const fallbackBudget = parsePercentValue(formValues[def.budgetField]);
                const fallbackAlert = parsePercentValue(formValues[def.alertField]);
                acc[def.budgetField] = value !== undefined ? value : fallbackBudget ?? 0;
                acc[def.alertField] = alert !== undefined ? alert : fallbackAlert ?? 0;
                return acc;
              }, {}),
              storage_recovery_budget: storageBudgetValue,
              storage_recovery_alert_threshold: storageAlertValue,
              notify_users: formValues.notify_users || [],
            };
            const existing = budgetsByLocation.get(location.location_id);
            if (existing) {
              return axiosInstance.put(`/api/v1/budget/${existing.id}`, payload);
            }
            return axiosInstance.post('/api/v1/budget', payload);
          })
        );
        await fetchData();
        setSuccess('Budget applied to all locations.');
      } catch (err) {
        const message = err?.response?.data?.error || 'Unable to apply budget to all locations.';
        setError(message);
      } finally {
        setSaving(false);
      }
    } else if (confirmDialog.action === 'copy-previous-location') {
      if (!selectedBudgetPeriod || !selectedLocation || !previousBudgetPeriod || !selectedLocationPreviousBudget) {
        setConfirmDialog({ open: false, action: null, title: '', message: '' });
        return;
      }

      setSaving(true);
      setError('');
      try {
        const payload = buildBudgetPayloadFromSource({
          budget: selectedLocationPreviousBudget,
          locationId: selectedLocation.location_id,
          period: selectedBudgetPeriod,
        });
        const existing = budgetsByLocation.get(selectedLocation.location_id);
        const response = existing
          ? await axiosInstance.put(`/api/v1/budget/${existing.id}`, payload)
          : await axiosInstance.post('/api/v1/budget', payload);
        const savedBudget = response?.data?.data;
        if (!savedBudget) {
          throw new Error('Unexpected response from server.');
        }
        setBudgets((prev) => {
          const next = prev.filter(
            (budget) =>
              !(
                budget.location_id === selectedLocation.location_id &&
                budget.budget_month === selectedBudgetPeriod.month &&
                budget.budget_year === selectedBudgetPeriod.year
              )
          );
          return [...next, savedBudget];
        });
        setSuccess(`Copied ${previousBudgetPeriod.label} budget for ${selectedLocation.name}.`);
      } catch (err) {
        const message = err?.response?.data?.error || 'Unable to copy the previous month budget.';
        setError(message);
      } finally {
        setSaving(false);
      }
    } else if (confirmDialog.action === 'copy-previous-all') {
      if (!selectedBudgetPeriod || !previousBudgetPeriod) {
        setConfirmDialog({ open: false, action: null, title: '', message: '' });
        return;
      }

      setSaving(true);
      setError('');
      try {
        await Promise.all(
          availablePreviousMonthBudgets.map((budget) => {
            const payload = buildBudgetPayloadFromSource({
              budget,
              locationId: budget.location_id,
              period: selectedBudgetPeriod,
            });
            const existing = budgetsByLocation.get(budget.location_id);
            if (existing) {
              return axiosInstance.put(`/api/v1/budget/${existing.id}`, payload);
            }
            return axiosInstance.post('/api/v1/budget', payload);
          })
        );
        await fetchData();
        setSuccess(`Copied ${previousBudgetPeriod.label} budgets to the selected month.`);
      } catch (err) {
        const message = err?.response?.data?.error || 'Unable to copy previous month budgets.';
        setError(message);
      } finally {
        setSaving(false);
      }
    }

    setConfirmDialog({ open: false, action: null, title: '', message: '' });
  };

  const handleAddDefaultEmail = () => {
    if (readOnly) return;
    const trimmed = defaultEmail.trim().toLowerCase();
    if (!trimmed) return;
    if (!emailPattern.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (defaultNotifyUsers.includes(trimmed)) {
      setDefaultEmail('');
      return;
    }
    setDefaultNotifyUsers((prev) => [...prev, trimmed]);
    setDefaultEmail('');
    setError('');
  };

  const handleDefaultEmailKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddDefaultEmail();
    }
  };

  const handleRemoveDefaultEmail = (email) => {
    if (readOnly) return;
    setDefaultNotifyUsers((prev) => prev.filter((item) => item !== email));
  };

  const isDefaultDirty = useMemo(() => {
    const current = normaliseStringArray(defaultNotifyUsers);
    const initial = normaliseStringArray(initialDefaultNotifyUsers);
    if (current.length !== initial.length) return true;
    for (let i = 0; i < current.length; i += 1) {
      if (current[i] !== initial[i]) return true;
    }
    return false;
  }, [defaultNotifyUsers, initialDefaultNotifyUsers]);

  const handleSaveDefaultNotify = async () => {
    if (readOnly) return;
    setDefaultSaving(true);
    setError('');
    try {
      const response = await axiosInstance.put('/api/v1/budget/settings', {
        default_notify_users: defaultNotifyUsers,
      });
      const settingsData = response?.data?.data || {};
      const savedDefaults = settingsData.default_notify_users || [];
      setDefaultNotifyUsers(savedDefaults);
      setInitialDefaultNotifyUsers(savedDefaults);
      setDefaultSettingsMeta({
        created_on: settingsData.created_on || '',
        created_by: settingsData.created_by || '',
        updated_on: settingsData.updated_on || '',
        updated_by: settingsData.updated_by || '',
      });
      setSuccess('Default notify users updated.');
    } catch (err) {
      const message = err?.response?.data?.error || 'Unable to update default notify users.';
      setError(message);
    } finally {
      setDefaultSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Budget Management
        </Typography>
        {readOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have read-only access. Editing controls are disabled.
          </Alert>
        )}
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={handleErrorClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: 14 }}
        >
          <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={Boolean(success)}
          autoHideDuration={4000}
          onClose={handleSuccessClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: 10 }}
        >
          <Alert onClose={handleSuccessClose} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
        <Paper sx={{ mb: 4, p: 3 }} elevation={2}>
          <Box
            sx={{
              borderBottom: 2,
              borderColor: 'divider',
              pb: 1,
              mb: 3,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Default Notify Users
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ minWidth: { sm: 320 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Updated By
                </Typography>
                <Typography variant="caption">
                  {defaultSettingsMeta.updated_by || '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Updated On
                </Typography>
                <Typography variant="caption">
                  {formatDateOnly(defaultSettingsMeta.updated_on)}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Email address"
              value={defaultEmail}
              onChange={(event) => setDefaultEmail(event.target.value)}
              onKeyDown={handleDefaultEmailKeyDown}
              placeholder="user@example.com"
              fullWidth
              disabled={readOnly}
            />
            <Button
              variant="outlined"
              onClick={handleAddDefaultEmail}
              disabled={readOnly || !defaultEmail.trim()}
            >
              Add
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveDefaultNotify}
              disabled={readOnly || defaultSaving || !isDefaultDirty}
            >
              Save
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ minHeight: 40, mb: 0 }}>
            {defaultNotifyUsers.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No default recipients configured.
              </Typography>
            )}
            {defaultNotifyUsers.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={readOnly ? undefined : () => handleRemoveDefaultEmail(email)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Stack>
        </Paper>

        <Paper sx={{ mt: 6, p: 3, position: 'relative' }} elevation={2}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box
            sx={{
              borderBottom: 2,
              borderColor: 'divider',
              pb: 1,
              mb: 3,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Location Budgets
            </Typography>
            {selectedLocation && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ minWidth: { sm: 320 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Updated By
                  </Typography>
                  <Typography variant="caption">
                    {formValues.updated_by || '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Updated On
                  </Typography>
                  <Typography variant="caption">
                    {formatDateOnly(formValues.updated_on)}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
          <Grid
            container
            spacing={2}
            sx={{ mb: 2, opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
          >
            <Grid item xs={12}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                sx={{ width: '100%', alignItems: { xs: 'stretch', lg: 'flex-end' } }}
              >
                <Autocomplete
                  options={budgetPeriodOptions}
                  value={selectedBudgetPeriod}
                  onChange={(_, newValue) => setSelectedBudgetPeriod(newValue)}
                  getOptionLabel={(option) => (option ? option.label : '')}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  sx={{ flex: '1 1 24rem', minWidth: 0 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Budget Period" placeholder="Select month and year" />
                  )}
                />
                <Button
                  variant="outlined"
                  onClick={handleCopyPreviousMonthForAllLocations}
                  disabled={readOnly || saving || uploadingBudget || !selectedBudgetPeriod || availablePreviousMonthBudgets.length === 0}
                  sx={{
                    width: { xs: '100%', lg: 'auto' },
                    minWidth: { lg: '20rem' },
                    minHeight: 56,
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  Copy Previous Month To All Sites
                </Button>
                <Button
                  variant="contained"
                  component="label"
                  disabled={readOnly || saving || uploadingBudget || !selectedBudgetPeriod}
                  sx={{
                    width: { xs: '100%', lg: 'auto' },
                    minWidth: { lg: '12rem' },
                    minHeight: 56,
                    flexShrink: 0,
                  }}
                >
                  {uploadingBudget ? 'Uploading...' : 'Upload Budget'}
                  <input
                    ref={budgetFileInputRef}
                    hidden
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleBudgetFileUpload}
                  />
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            sx={{ mb: 4, opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
          >
            <Grid item xs={12} sm={6} md={6}>
              <Autocomplete
                options={locations}
                value={selectedLocation}
                onChange={(_, newValue) => setSelectedLocation(newValue)}
                getOptionLabel={(option) => (option ? `${option.location_id} — ${option.name}` : '')}
                isOptionEqualToValue={(option, value) => option.location_id === value?.location_id}
                renderInput={(params) => (
                  <TextField {...params} label="Location" placeholder="Select a location" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', lg: 'flex-start' }}
                sx={{ width: '100%' }}
              >
                <TextField
                  label="Site Budget"
                  value={
                    isSiteBudgetFocused
                      ? formValues.site_budget
                      : formatSiteBudgetDisplay(formValues.site_budget)
                  }
                  onChange={(event) => handleSiteBudgetChange(event.target.value)}
                  onFocus={() => setIsSiteBudgetFocused(true)}
                  onBlur={() => setIsSiteBudgetFocused(false)}
                  fullWidth
                  inputProps={{ inputMode: 'decimal' }}
                  helperText="Max 6 digits before decimal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                  disabled={readOnly}
                  sx={{ flex: '1 1 16rem', minWidth: 0 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleCopyPreviousMonthForLocation}
                  disabled={readOnly || saving || !selectedLocation || !selectedBudgetPeriod || !selectedLocationPreviousBudget}
                  sx={{
                    alignSelf: { xs: 'stretch', lg: 'flex-start' },
                    width: { xs: '100%', lg: 'auto' },
                    minWidth: { lg: '14rem' },
                    minHeight: 56,
                    maxWidth: '100%',
                    mt: { lg: 0.5 },
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  Copy Previous Month
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {!selectedLocation ? (
            <Typography color="text.secondary">Choose a location to manage its budget settings.</Typography>
          ) : (
            <Box sx={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Parts Budget %"
                    value={formValues.parts_budget}
                    onChange={(event) => handlePartsBudgetChange(event.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    helperText="Enter percentage (0-100)"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Parts Alert Threshold %"
                    value={formValues.parts_alert_threshold}
                    onChange={(event) => handlePartsAlertChange(event.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    helperText="Enter percentage (0-100)"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderLeft: 3,
                      borderColor: 'divider',
                      pl: 2,
                      ml: { xs: 0, md: 1 },
                      py: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Parts Budget Split (must total 100%)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Genuine Parts Budget %"
                          value={formValues.genuine_parts_budget}
                          onChange={(event) => handleGenuineBudgetChange(event.target.value)}
                          fullWidth
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                          helperText="Enter percentage (0-100)"
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Genuine Parts Alert Threshold %"
                          value={formValues.genuine_parts_alert_threshold}
                          onChange={(event) => handleGenuineAlertChange(event.target.value)}
                          fullWidth
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                          helperText="Enter percentage (0-100)"
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Non Genuine Parts Budget %"
                          value={formValues.non_genuine_parts_budget}
                          onChange={(event) => handleNonGenuineBudgetChange(event.target.value)}
                          fullWidth
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                          helperText="Enter percentage (0-100)"
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Non Genuine Parts Alert Threshold %"
                          value={formValues.non_genuine_parts_alert_threshold}
                          onChange={(event) => handleNonGenuineAlertChange(event.target.value)}
                          fullWidth
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                          helperText="Enter percentage (0-100)"
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Green Parts Budget %"
                          value={formValues.green_parts_budget}
                          onChange={(event) => handleGreenBudgetChange(event.target.value)}
                          fullWidth
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                          helperText="Enter percentage (0-100)"
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Green Parts Alert Threshold %"
                          value={formValues.green_parts_alert_threshold}
                          onChange={(event) => handleGreenAlertChange(event.target.value)}
                          fullWidth
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                          helperText="Enter percentage (0-100)"
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          disabled={readOnly}
                        />
                      </Grid>
                      {isPartsSplitInvalid && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="error">
                            Genuine, non genuine, and green parts budgets must total 100%.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Subcontracted Budget %"
                    value={formValues.subcontracted_budget}
                    onChange={(event) => handleSubcontractedBudgetChange(event.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    helperText="Enter percentage (0-100)"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Subcontracted Alert Threshold %"
                    value={formValues.subcontracted_alert_threshold}
                    onChange={(event) => handleSubcontractedAlertChange(event.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    helperText="Enter percentage (0-100)"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    disabled={readOnly}
                  />
                </Grid>
                {excludedSubcontractedDefs.map((def) => (
                  <React.Fragment key={def.budgetField}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={`${def.label} Budget %`}
                        value={formValues[def.budgetField] ?? ''}
                        onChange={(event) => handleSubcontractedChildBudgetChange(def, event.target.value)}
                        fullWidth
                        inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                        helperText="Enter percentage (0-100)"
                        InputLabelProps={{ shrink: (formValues[def.budgetField] ?? '') !== '' }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        disabled={readOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={`${def.label} Alert Threshold %`}
                        value={formValues[def.alertField] ?? ''}
                        onChange={(event) => handleSubcontractedChildAlertChange(def, event.target.value)}
                        fullWidth
                        inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                        helperText="Enter percentage (0-100)"
                        InputLabelProps={{ shrink: (formValues[def.alertField] ?? '') !== '' }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        disabled={readOnly}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Storage & Recovery Budget %"
                    value={formValues.storage_recovery_budget}
                    onChange={(event) => handleStorageBudgetChange(event.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    helperText="Enter percentage (0-100)"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Storage & Recovery Alert Threshold %"
                    value={formValues.storage_recovery_alert_threshold}
                    onChange={(event) => handleStorageAlertChange(event.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    helperText="Enter percentage (0-100)"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    disabled={readOnly}
                  />
                </Grid>
                {isBudgetSumInvalid && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="error">
                      Combined parts, subcontracted, excluded subcontracted categories, and storage & recovery budgets must be ≤ 100%.
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'left' }}>
                    Notify Users
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1 }}>
                    <TextField
                      label="Email address"
                      value={newEmail}
                      onChange={(event) => setNewEmail(event.target.value)}
                      onKeyDown={handleEmailKeyDown}
                      placeholder="user@example.com"
                      fullWidth
                      disabled={readOnly}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddEmail}
                      disabled={readOnly || !newEmail.trim()}
                    >
                      Add
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ minHeight: 40 }}>
                    {formValues.notify_users?.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No recipients configured.
                      </Typography>
                    )}
                    {formValues.notify_users?.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={readOnly ? undefined : () => handleRemoveEmail(email)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={
                    readOnly ||
                    saving ||
                    formValues.site_budget === '' ||
                    formValues.parts_budget === '' ||
                    formValues.parts_alert_threshold === '' ||
                    formValues.genuine_parts_budget === '' ||
                    formValues.genuine_parts_alert_threshold === '' ||
                    formValues.non_genuine_parts_budget === '' ||
                    formValues.non_genuine_parts_alert_threshold === '' ||
                    formValues.green_parts_budget === '' ||
                    formValues.green_parts_alert_threshold === '' ||
                    formValues.subcontracted_budget === '' ||
                    formValues.subcontracted_alert_threshold === '' ||
                    excludedSubcontractedDefs.some(
                      (def) => formValues[def.budgetField] === '' || formValues[def.alertField] === ''
                    ) ||
                    formValues.storage_recovery_budget === '' ||
                    formValues.storage_recovery_alert_threshold === '' ||
                    isBudgetSumInvalid ||
                    isPartsSplitInvalid ||
                    !isFormDirty
                  }
                >
                  {formValues.budgetId ? 'Update Budget' : 'Create Budget'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={readOnly || !formValues.budgetId || saving}
                >
                  Delete
                </Button>
                <Button
                  variant="text"
                  onClick={handleApplyAll}
                  disabled={
                    readOnly ||
                    saving ||
                    formValues.site_budget === '' ||
                    formValues.parts_budget === '' ||
                    formValues.parts_alert_threshold === '' ||
                    formValues.genuine_parts_budget === '' ||
                    formValues.genuine_parts_alert_threshold === '' ||
                    formValues.non_genuine_parts_budget === '' ||
                    formValues.non_genuine_parts_alert_threshold === '' ||
                    formValues.green_parts_budget === '' ||
                    formValues.green_parts_alert_threshold === '' ||
                    formValues.subcontracted_budget === '' ||
                    formValues.subcontracted_alert_threshold === '' ||
                    excludedSubcontractedDefs.some(
                      (def) => formValues[def.budgetField] === '' || formValues[def.alertField] === ''
                    ) ||
                    formValues.storage_recovery_budget === '' ||
                    formValues.storage_recovery_alert_threshold === '' ||
                    isPartsSplitInvalid ||
                    isBudgetSumInvalid
                  }
                >
                  Apply to All Locations
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>
      </Paper>
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, title: '', message: '' })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, title: '', message: '' })}>Cancel</Button>
          <Button onClick={executeConfirmedAction} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetPage;
